import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  groupOperations,
  listOperations,
  type TimelineStep,
} from '../../data/versionRepo';
import { invertBatch } from '../ops/operation';
import { useUserDirectory } from '../../hooks/useUserDirectory';
import type { Scene } from '../scene';

/**
 * 타임라인 스크럽 (작업지시서 P4-10)
 *
 * op-log 를 되감아 "이 도면이 어떻게 만들어졌는지" 를 재생한다.
 *
 * ── 왜 스냅샷을 다시 그리지 않고 되감는가 ───────────────────────────────
 *
 *   버전마다 스냅샷을 저장하면 명령 단위로는 못 본다. 검토자가 알고 싶은 건
 *   "누가 언제 이 벽을 옮겼나" 이고, 그건 연산 로그에만 있다.
 *
 * ── 반드시 지켜야 하는 것 ───────────────────────────────────────────────
 *
 *   **스크럽을 앞뒤로 흔들어도 도면이 어긋나지 않는다.**
 *   현재 위치에서 목표 위치까지 필요한 만큼만 적용/역적용한다.
 *   매번 처음부터 다시 계산하면 큰 도면에서 화면이 멎는다.
 *
 *   되감기는 **역순**으로 적용해야 한다. 순서대로 뒤집으면 서로 의존하는
 *   연산이 깨진다 (invertBatch 가 이미 ops 를 뒤집는다).
 */

export function Timeline({
  versionId,
  scene,
  onClose,
}: {
  versionId: string;
  scene: Scene;
  onClose: () => void;
}) {
  const { nameOf } = useUserDirectory();
  const [steps, setSteps] = useState<TimelineStep[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pos, setPos] = useState(0);
  const [playing, setPlaying] = useState(false);

  /** 화면에 실제로 반영된 위치. pos 와 벌어진 만큼만 따라잡는다. */
  const applied = useRef(0);

  useEffect(() => {
    let alive = true;
    listOperations(versionId)
      .then((rows) => {
        if (!alive) return;
        const s = groupOperations(rows);
        setSteps(s);
        // 처음에는 "지금 상태" = 전부 적용된 상태다
        setPos(s.length);
        applied.current = s.length;
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [versionId]);

  /** 목표 위치까지 필요한 만큼만 적용/역적용한다 */
  const seek = useCallback(
    (target: number) => {
      if (!steps) return;
      const clamped = Math.max(0, Math.min(steps.length, target));

      // ★ record=false. 되감기는 **보기**이지 편집이 아니다.
      //   true 로 두면 pending 에 쌓여 자동 저장이 과거 상태를 덮어쓴다.
      while (applied.current < clamped) {
        scene.applyBatch(steps[applied.current]!.batch, false);
        applied.current++;
      }
      while (applied.current > clamped) {
        applied.current--;
        scene.applyBatch(invertBatch(steps[applied.current]!.batch), false);
      }

      setPos(clamped);
    },
    [steps, scene]
  );

  /**
   * 타임라인을 닫을 때는 반드시 현재 상태로 되돌린다.
   * 과거 시점인 채로 편집을 재개하면 그 뒤 작업이 통째로 사라진다.
   */
  const restoreAndClose = useCallback(() => {
    if (steps) seek(steps.length);
    setPlaying(false);
    onClose();
  }, [steps, seek, onClose]);

  // 컴포넌트가 어떤 이유로든 사라지면 원상 복구한다
  useEffect(() => {
    return () => {
      if (!steps) return;
      while (applied.current < steps.length) {
        scene.applyBatch(steps[applied.current]!.batch, false);
        applied.current++;
      }
    };
  }, [steps, scene]);

  // 재생
  useEffect(() => {
    if (!playing || !steps) return;
    if (pos >= steps.length) {
      setPlaying(false);
      return;
    }
    const t = window.setTimeout(() => seek(pos + 1), 450);
    return () => window.clearTimeout(t);
  }, [playing, pos, steps, seek]);

  const current = useMemo(
    () => (steps && pos > 0 ? steps[pos - 1] : null),
    [steps, pos]
  );

  if (error) {
    return (
      <div className="timeline" role="region" aria-label="작업 되감기">
        <p className="timeline__err">이력을 불러오지 못했습니다: {error}</p>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
          닫기
        </button>
      </div>
    );
  }

  if (!steps) {
    return (
      <div className="timeline">
        <span className="timeline__label">이력을 불러오는 중…</span>
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <div className="timeline">
        <span className="timeline__label">
          이 버전에는 기록된 작업이 없습니다. 편집을 저장하면 이력이 쌓입니다.
        </span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
          닫기
        </button>
      </div>
    );
  }

  return (
    <div className="timeline" role="region" aria-label="작업 되감기">
      <div className="timeline__controls">
        <button
          type="button"
          className="timeline__btn"
          title="처음으로"
          onClick={() => {
            setPlaying(false);
            seek(0);
          }}
        >
          ⏮
        </button>
        <button
          type="button"
          className="timeline__btn"
          title="이전 작업"
          onClick={() => {
            setPlaying(false);
            seek(pos - 1);
          }}
        >
          ◀
        </button>
        <button
          type="button"
          className="timeline__btn timeline__btn--play"
          title={playing ? '일시정지' : '재생'}
          onClick={() => setPlaying((p) => !p)}
        >
          {playing ? '⏸' : '▶'}
        </button>
        <button
          type="button"
          className="timeline__btn"
          title="다음 작업"
          onClick={() => {
            setPlaying(false);
            seek(pos + 1);
          }}
        >
          ▶|
        </button>
        <button
          type="button"
          className="timeline__btn"
          title="지금으로"
          onClick={() => {
            setPlaying(false);
            seek(steps.length);
          }}
        >
          ⏭
        </button>
      </div>

      <input
        className="timeline__range"
        type="range"
        min={0}
        max={steps.length}
        value={pos}
        aria-label="작업 위치"
        aria-valuetext={
          current ? `${pos}/${steps.length} ${current.batch.command}` : '작업 전'
        }
        onChange={(e) => {
          setPlaying(false);
          seek(Number(e.target.value));
        }}
      />

      <span className="timeline__label">
        {pos} / {steps.length}
        {current && (
          <>
            {' · '}
            <strong>{current.batch.command}</strong>
            {' · '}
            <span title={current.actor}>{nameOf(current.actor)}</span>
            {' · '}
            {new Date(current.batch.at).toLocaleString('ko-KR')}
          </>
        )}
        {pos === 0 && ' · 작업 전 상태'}
      </span>

      {pos < steps.length && (
        // 과거 시점이라는 걸 반드시 알린다 — 모르고 편집하면 이후 작업이 날아간다
        <span className="timeline__past">과거 시점 · 편집 불가</span>
      )}

      <button type="button" className="btn btn--ghost btn--sm" onClick={restoreAndClose}>
        닫기
      </button>
    </div>
  );
}
